import React, {useEffect, useRef, useState} from 'react';
import {RootState, useAppDispatch} from 'src/store';
import {NavigateFunction, useLocation, useNavigate} from 'react-router-dom';
import {GOOGLE_OAUTH_CLIENT_ID} from '../../constants';
import {GOOGLE_OAUTH_SCOPES} from '../../constants/auth';
import {ReactComponent as GoogleIcon} from 'src/assets/icons/google.svg';
import {toast, ToastContainer} from 'react-toastify';
import {useSelector} from 'react-redux';
import {twMerge} from 'tailwind-merge';
import {getAccessToken, getAccessTokenFromGoogleAuth} from '../../actions/pure/auth';
import {logout} from '../../actions/auth';

const onLoginSuccess = (navigate: NavigateFunction) => {
    toast.success('Logged in successfully');
    navigate('/');
};

const LoginForm = () => {
    let [email, setEmail] = useState('');
    let [password, setPassword] = useState('');
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const auth = useSelector((state: RootState) => state.auth);

    function onGoogleSigninClick() {
        const redirectUrl = window.location.origin + '/dashboard/_callback';
        window.location.href =
            'https://accounts.google.com/o/oauth2/v2/auth?' +
            [
                `scope=${GOOGLE_OAUTH_SCOPES}`,
                'include_granted_scopes=true',
                'response_type=code',
                `redirect_uri=${redirectUrl}`,
                `client_id=${GOOGLE_OAUTH_CLIENT_ID}`,
                'access_type=offline',
            ].join('&');
    }

    function onLoginSubmit() {
        dispatch(
            getAccessToken({
                email,
                password,
            }),
        ).then((data) => {
            if (data.type === getAccessToken.fulfilled.type) {
                onLoginSuccess(navigate);
            } else if (data.type === getAccessToken.rejected.type) {
                toast.error('Failed to Login');
            }
        });
    }

    return (
        <>
            <ToastContainer position={'top-right'} autoClose={2000} />
            <div className={'min-w-96 rounded-lg border border-gray-900'}>
                <div className={'flex flex-col p-5'}>
                    <span className={'text-2xl font-bold'}>Login</span>
                    <hr />
                    <div
                        className={
                            'mt-5 flex cursor-pointer flex-row items-center justify-center gap-2 rounded-md bg-gray-200 p-2 text-black'
                        }>
                        <button onClick={onGoogleSigninClick} className={'flex flex-row p-2'}>
                            <GoogleIcon className={'h-5 w-5'} />
                            <span className={'pl-2'}>Sign in with Google</span>
                        </button>
                    </div>
                    <hr />
                    <div className={'mt-5 flex flex-col'}>
                        <div className={'flex flex-col'}>
                            <label>Email</label>
                            <input
                                className={
                                    'rounded-md border border-gray-900 p-2 transition duration-300 ease-in-out focus:border-blue-500 focus:outline-none'
                                }
                                type={'text'}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div className={'flex flex-col'}>
                            <label>Password</label>
                            <input
                                className={
                                    'rounded-md border border-gray-900 p-2 transition duration-300 ease-in-out focus:border-blue-500 focus:outline-none'
                                }
                                type={'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && onLoginSubmit()}
                            />
                        </div>
                        <button
                            onClick={onLoginSubmit}
                            className={twMerge(
                                'mt-5 rounded-md bg-blue-500 p-2 text-white transition duration-300 ease-in-out hover:bg-blue-600',
                                auth.authLoading ? ' cursor-wait bg-gray-500 hover:bg-gray-600' : '',
                            )}
                            disabled={auth.authLoading}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export const LoginComponent = () => {
    return (
        <div className={'flex min-h-screen flex-row items-center justify-center'}>
            <LoginForm />
        </div>
    );
};

export const LoginCallbackComponent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {search} = location;
    const ref = useRef(false);
    const [error, setError] = useState('');
    const dispatch = useAppDispatch();
    const auth = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (ref.current) {
            return;
        }
        ref.current = true;

        const code = new URLSearchParams(search).get('code');
        if (!code) {
            setError("Couldn't get code from Google");
        } else {
            dispatch(
                getAccessTokenFromGoogleAuth({
                    code,
                }),
            ).then((event) => {
                if (event.type === getAccessTokenFromGoogleAuth.fulfilled.type) {
                    // we don't have a ToastContainer on this component, but that's fine
                    onLoginSuccess(navigate);
                } else if (event.type === getAccessTokenFromGoogleAuth.rejected.type) {
                    setError('Failed to login');
                } else {
                    setError('Unknown error');
                }
            });
        }
    }, [search, dispatch, navigate]);

    return (
        <div className={'flex min-h-screen flex-row items-center justify-center'}>
            {error ? (
                <div className={'p-5 text-center text-red-500'}>
                    <span>{error}</span>
                    <br />
                    <span className={'text-sm'}>{auth.error}</span>
                </div>
            ) : (
                <div className={'p-5'}>
                    <span>Logging in...</span>
                </div>
            )}
        </div>
    );
};

export const LogoutComponent = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(logout()).then(() => {
            navigate('/');
        });
    }, [dispatch, navigate]);

    return (
        <div className={'flex min-h-screen flex-row items-center justify-center'}>
            <div className={'p-5'}>
                <span>Logging out...</span>
            </div>
        </div>
    );
};
